import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { AdminRole, AdminUser, AuthState } from '../types/auth';
import {
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
} from '../types/auth';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import { SessionExpiryWarning } from '../components/ui/SessionExpiryWarning';
import * as adminAuth from '../services/adminAuthRedesignService';
import { browserNavigation } from '../utils/browserNavigation';

interface AuthContextType extends AuthState {
  sessionUnavailable: boolean;
  logout: (reason?: 'inactivity' | 'session_expired') => Promise<void>;
  logoutAll: () => Promise<void>;
  hydrateAdminSession: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });
  const [warningOpen, setWarningOpen] = useState(false);
  const loggingOutRef = useRef(false);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const hydrationRef = useRef<Promise<void> | null>(null);
  const generationRef = useRef(0);
  const lastSessionCheckRef = useRef(Date.now());

  const hydrateAdminSession = useCallback((): Promise<void> => {
    if (hydrationRef.current) return hydrationRef.current;
    const generation = generationRef.current;
    const operation = (async () => {
      const session = await adminAuth.getAdminSession();
      if (
        !session.authenticated ||
        session.readiness !== 'READY' ||
        !session.mfaSatisfied ||
        session.roles.length === 0
      ) {
        throw { status: 401, code: 'AUTH_SESSION_EXPIRED' };
      }
      if (generation !== generationRef.current) return;
      const role = session.roles[0] as AdminRole;
      const user: AdminUser = {
        id: session.userId,
        email: session.email ?? '',
        name:
          [session.firstName, session.lastName].filter(Boolean).join(' ') ||
          session.email ||
          'Administrator',
        role,
        roles: session.roles as AdminRole[],
        permissions: session.permissions,
        mfaEnabled: true,
      };
      setSessionUnavailable(false);
      lastSessionCheckRef.current = Date.now();
      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    })()
      .catch((error: unknown) => {
        if (generation !== generationRef.current) return;
        const status =
          typeof error === 'object' && error !== null && 'status' in error
            ? error.status
            : undefined;
        const expired = status === 401 || status === 403;
        setSessionUnavailable(!expired);
        if (expired) setWarningOpen(false);
        setState(previous =>
          expired
            ? {
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
              }
            : {
                ...previous,
                isLoading: false,
                error: 'Session check is temporarily unavailable.',
              }
        );
        throw error;
      })
      .finally(() => {
        hydrationRef.current = null;
      });
    hydrationRef.current = operation;
    return operation;
  }, []);

  useEffect(() => {
    void hydrateAdminSession().catch(() => undefined);
  }, [hydrateAdminSession]);

  const checkActiveSession = useCallback(() => {
    // Renew only after user activity; a background interval must never defeat idle expiry.
    if (Date.now() - lastSessionCheckRef.current < 2 * 60 * 1000) return;
    lastSessionCheckRef.current = Date.now();
    void hydrateAdminSession().catch(() => undefined);
  }, [hydrateAdminSession]);

  const logout = useCallback(
    async (reason?: 'inactivity' | 'session_expired') => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;
      generationRef.current += 1;
      setSessionUnavailable(false);
      try {
        await adminAuth.logoutAdminSession();
      } finally {
        setWarningOpen(false);
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
        loggingOutRef.current = false;
        if (window.location.pathname !== '/login') {
          browserNavigation.assign(
            reason ? `/login?reason=${reason}` : '/login'
          );
        }
      }
    },
    []
  );

  const logoutAll = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    generationRef.current += 1;
    setSessionUnavailable(false);
    try {
      await adminAuth.logoutAllAdminSessions();
    } finally {
      setWarningOpen(false);
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      loggingOutRef.current = false;
      if (window.location.pathname !== '/login') {
        browserNavigation.assign('/login?reason=global-logout');
      }
    }
  }, []);

  useInactivityTimer({
    enabled: state.isAuthenticated,
    timeoutMs: 30 * 60 * 1000,
    warningBeforeMs: 5 * 60 * 1000,
    onActivity: checkActiveSession,
    onTimeout: () => void logout('inactivity'),
    onWarning: () => setWarningOpen(true),
    onActive: () => setWarningOpen(false),
  });

  const clearError = useCallback(() => {
    setState(previous => ({ ...previous, error: null }));
  }, []);

  const value: AuthContextType = {
    ...state,
    sessionUnavailable,
    logout,
    logoutAll,
    hydrateAdminSession,
    hasPermission: permission => hasPermission(state.user, permission),
    hasAnyPermission: permissions => hasAnyPermission(state.user, permissions),
    hasAllPermissions: permissions =>
      hasAllPermissions(state.user, permissions),
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      <SessionExpiryWarning
        isOpen={warningOpen}
        reason="inactivity"
        onDismiss={() => setWarningOpen(false)}
      />
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
